import { useState, useEffect } from "react";
import { supabase } from "../supabase/supabase";
import { SEASON_YEAR } from "../utils/season";
import { getCached, getStale, setCached, STATIC_TTL_MS } from "../utils/queryCache";

// Preseason rankings and projections for the season. They are set before the
// season starts and edited only between seasons, so this is the most static
// table the app reads, and it was the one being refetched in full on every
// mount of every component that wanted it.
//
// It used to keep its own localStorage entry and refetch regardless. Now it
// goes through the shared cache, which means the refetch happens once a day
// rather than once a mount, the stale copy still paints immediately on a cold
// launch, and sign-out clears it along with everything else.
export function usePreseasonStats() {
  // v2: devices that cached an empty map under the old key would keep serving
  // it until its 24 hour TTL expired, even with the bug fixed. Renaming the
  // key abandons those entries on the next launch instead.
  const cacheKey = `preseason:v2:${SEASON_YEAR}`;

  const [data, setData] = useState(() => getCached(cacheKey) || getStale(cacheKey));
  const [loading, setLoading] = useState(() => !getCached(cacheKey));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (getCached(cacheKey)) {
      setLoading(false);
      return;
    }
    supabase
      .from("team_preseason_stats")
      .select("*")
      .eq("season_year", SEASON_YEAR)
      .then(({ data: rows, error: err }) => {
        // A failed or empty fetch must not be cached. This is persisted for a
        // day, so writing {} here meant one bad launch (offline, a dropped
        // request on a Capacitor cold start) left every stat column showing
        // "-" until the TTL expired, with no refetch to correct it. Keep
        // whatever is already on screen instead and try again next mount.
        if (err || !rows?.length) {
          setError(err?.message || "Preseason stats unavailable");
          setLoading(false);
          return;
        }
        const map = {};
        rows.forEach((r) => { map[r.team_id] = r; });
        setCached(cacheKey, map, STATIC_TTL_MS, { persist: true });
        setError(null);
        setData(map);
        setLoading(false);
      });
  }, [cacheKey]);

  return { preseasonData: data || {}, loading, error };
}
